// TEMPORARY DEMO UI — safe to delete.
//
// A self-contained, interactive "Demo Mode" that showcases 5 demo restaurants
// and 5 demo NGO partners using sample data (see demoData.ts). It reuses the
// shared pure domain functions (matching, reservation CAS, collection, impact
// aggregation, rating averages) so the behaviour mirrors the real app, but it
// never calls Firebase or the backend — claims are held in local React state.
// To remove the demo entirely, delete the `app/src/demo` folder and the demo
// wiring in `app/src/App.tsx`.

import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  attemptReserve,
  computeRatingAverage,
  eligibleNGOs,
  markCollected,
  totalImpactMeals,
  type Donation,
  type FoodCategory,
  type NGOProfile,
  type RestaurantProfile,
} from "@sharebite/domain";
import { Card, Stat } from "../components/primitives";
import { DonationRow } from "../components/DonationCard";
import {
  DEMO_DONATIONS,
  DEMO_NGOS,
  DEMO_NOW,
  DEMO_RESTAURANTS,
  ngoById,
  ratingsForNGO,
  restaurantById,
} from "./demoData";

type Selection =
  | { kind: "home" }
  | { kind: "restaurant"; uid: string }
  | { kind: "ngo"; uid: string };

export function DemoApp({
  onExit,
  brand,
}: {
  onExit: () => void;
  brand?: ReactNode;
}): JSX.Element {
  const [selection, setSelection] = useState<Selection>({ kind: "home" });
  // Live, mutable donation list so claims/collections persist within the demo.
  const [donations, setDonations] = useState<Donation[]>(() =>
    DEMO_DONATIONS.map((d) => ({ ...d })),
  );

  function claim(donationId: string, ngoUid: string): void {
    setDonations((prev) =>
      prev.map((d) => {
        if (d.donationId !== donationId) return d;
        const outcome = attemptReserve(
          { status: d.status, reservedBy: d.reservedBy, reservedAt: d.reservedAt },
          ngoUid,
          DEMO_NOW,
        );
        return outcome.ok ? { ...d, ...outcome.state } : d;
      }),
    );
  }

  function collect(donationId: string, ngoUid: string): void {
    setDonations((prev) =>
      prev.map((d) => {
        if (d.donationId !== donationId) return d;
        const result = markCollected(
          { status: d.status, reservedBy: d.reservedBy },
          ngoUid,
        );
        return result.ok ? { ...d, status: result.status } : d;
      }),
    );
  }

  // Restaurant publishes a new surplus donation — it becomes Available and
  // immediately claimable by NGOs whose Accepted Categories match.
  const seq = useRef(0);
  function publish(
    restaurantUid: string,
    input: { foodName: string; foodCategory: FoodCategory; mealCount: number; description: string },
  ): void {
    seq.current += 1;
    const donation: Donation = {
      donationId: `demo-new-${seq.current}`,
      restaurantUid,
      foodName: input.foodName,
      description: input.description,
      imageRefs: ["demo/placeholder.jpg"],
      mealCount: input.mealCount,
      foodCategory: input.foodCategory,
      preparationTime: DEMO_NOW - 30 * 60 * 1000,
      pickupDeadline: DEMO_NOW + 2 * 60 * 60 * 1000,
      specialInstructions: "",
      status: "Available",
      reservedBy: null,
      reservedAt: null,
      createdAt: DEMO_NOW,
    };
    setDonations((prev) => [donation, ...prev]);
  }

  return (
    <div className="sb-theme-red">
      <header className="sb-appbar">
        <div className="sb-appbar__inner">
          {brand ?? <strong>ShareBite — Demo Mode</strong>}
          <span className="sb-spacer" />
          {selection.kind !== "home" && (
            <button
              type="button"
              className="sb-btn sb-btn--ghost"
              onClick={() => setSelection({ kind: "home" })}
            >
              ← Demo home
            </button>
          )}
          <button type="button" className="sb-btn sb-btn--secondary" onClick={onExit}>
            Exit demo
          </button>
        </div>
      </header>

      <div className="sb-main">
        <p className="sb-section-intro">
          Sample data for exploration only — nothing here is saved and no sign-in
          is required.
        </p>

        {selection.kind === "home" && <DemoHome onSelect={setSelection} />}
        {selection.kind === "restaurant" && (
          <RestaurantDemo
            key={selection.uid}
            uid={selection.uid}
            donations={donations}
            onCreate={publish}
          />
        )}
        {selection.kind === "ngo" && (
          <NGODemo
            key={selection.uid}
            uid={selection.uid}
            donations={donations}
            onClaim={claim}
            onCollect={collect}
          />
        )}
      </div>
    </div>
  );
}

function CatPill({ value }: { value: string }): JSX.Element {
  return (
    <span className="sb-pill" data-cat={value}>
      {value}
    </span>
  );
}

/** Visible phone number rendered as a click-to-call link. */
function Phone({ number, label }: { number: string; label?: string }): JSX.Element {
  return (
    <a className="sb-phone" href={`tel:${number}`}>
      <span aria-hidden>📞</span>
      {label ? `${label}: ` : ""}
      {number}
    </a>
  );
}

function DemoHome({
  onSelect,
}: {
  onSelect: (s: Selection) => void;
}): JSX.Element {
  return (
    <>
      <Card title="Demo Restaurants">
        <p className="sb-section-intro">Select a restaurant to see its donations and impact.</p>
        {DEMO_RESTAURANTS.map((r) => (
          <button
            key={r.uid}
            type="button"
            className="sb-list-item sb-selectable"
            onClick={() => onSelect({ kind: "restaurant", uid: r.uid })}
          >
            <div className="sb-row">
              <strong>{r.restaurantName}</strong>
              <span className="sb-spacer" />
              {r.foodCategoriesServed.map((c) => (
                <CatPill key={c} value={c} />
              ))}
            </div>
            <div className="sb-row" style={{ marginTop: 6 }}>
              <span className="sb-muted">{r.address}</span>
              <span className="sb-spacer" />
              <Phone number={r.phoneNumber} />
            </div>
          </button>
        ))}
      </Card>

      <Card title="Demo NGO partners">
        <p className="sb-section-intro">Select an NGO to see donations matched to it.</p>
        {DEMO_NGOS.map((n) => (
          <button
            key={n.uid}
            type="button"
            className="sb-list-item sb-selectable"
            onClick={() => onSelect({ kind: "ngo", uid: n.uid })}
          >
            <div className="sb-row">
              <strong>{n.ngoName}</strong>
              <span className="sb-spacer" />
              {n.acceptedCategories.map((c) => (
                <CatPill key={c} value={c} />
              ))}
            </div>
            <div className="sb-row" style={{ marginTop: 6 }}>
              <span className="sb-muted">{n.address}</span>
              <span className="sb-spacer" />
              <Phone number={n.phoneNumber} />
            </div>
          </button>
        ))}
      </Card>
    </>
  );
}

function RestaurantDemo({
  uid,
  donations,
  onCreate,
}: {
  uid: string;
  donations: Donation[];
  onCreate: (
    restaurantUid: string,
    input: { foodName: string; foodCategory: FoodCategory; mealCount: number; description: string },
  ) => void;
}): JSX.Element {
  const restaurant = restaurantById(uid) as RestaurantProfile;
  const mine = useMemo(
    () => donations.filter((d) => d.restaurantUid === uid),
    [donations, uid],
  );
  const active = mine.filter(
    (d) => d.status === "Available" || d.status === "Reserved",
  );
  const history = mine.filter(
    (d) => d.status === "Completed" || d.status === "Expired" || d.status === "Cancelled",
  );
  const completedCount = mine.filter((d) => d.status === "Completed").length;
  const mealsDonated = totalImpactMeals(mine);

  // Create-donation form state.
  const [foodName, setFoodName] = useState("");
  const [category, setCategory] = useState<FoodCategory>(
    restaurant.foodCategoriesServed[0],
  );
  const [mealCount, setMealCount] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [justPublished, setJustPublished] = useState<string | null>(null);

  function submit(): void {
    const n = Number(mealCount);
    if (!foodName.trim()) {
      setError("Please enter a food name.");
      return;
    }
    if (!Number.isInteger(n) || n < 1 || n > 9999) {
      setError("Meal count must be a whole number from 1 to 9,999.");
      return;
    }
    onCreate(uid, {
      foodName: foodName.trim(),
      foodCategory: category,
      mealCount: n,
      description: description.trim(),
    });
    setJustPublished(foodName.trim());
    setFoodName("");
    setMealCount("");
    setDescription("");
    setError(null);
  }

  return (
    <>
      <Card title={restaurant.restaurantName}>
        <p className="sb-muted" style={{ marginTop: 0 }}>
          Owner: {restaurant.ownerName} · {restaurant.address}
        </p>
        <div className="sb-row">
          {restaurant.foodCategoriesServed.map((c) => (
            <CatPill key={c} value={c} />
          ))}
          <span className="sb-spacer" />
          <Phone number={restaurant.phoneNumber} label="Contact" />
        </div>
      </Card>

      <Card title="Overview">
        <div className="sb-stats">
          <Stat label="Total donations" value={mine.length} />
          <Stat label="Active" value={active.length} />
          <Stat label="Completed" value={completedCount} />
          <Stat label="Meals donated" value={mealsDonated} />
        </div>
      </Card>

      <Card title="Upload a donation">
        <p className="sb-section-intro">
          Publish surplus food. It instantly becomes available for matching NGOs to claim.
        </p>
        <div className="sb-field">
          <label htmlFor="don-name">Food name</label>
          <input
            id="don-name"
            className="sb-input"
            value={foodName}
            onChange={(e) => setFoodName(e.target.value)}
            placeholder="e.g. Vegetable biryani trays"
            maxLength={100}
          />
        </div>
        <div className="sb-field">
          <label htmlFor="don-cat">Food category</label>
          <select
            id="don-cat"
            className="sb-select"
            value={category}
            onChange={(e) => setCategory(e.target.value as FoodCategory)}
          >
            {restaurant.foodCategoriesServed.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="sb-field">
          <label htmlFor="don-meals">Meal count</label>
          <input
            id="don-meals"
            className="sb-input"
            type="number"
            min={1}
            max={9999}
            value={mealCount}
            onChange={(e) => setMealCount(e.target.value)}
            placeholder="e.g. 30"
          />
        </div>
        <div className="sb-field">
          <label htmlFor="don-desc">Description (optional)</label>
          <textarea
            id="don-desc"
            className="sb-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Freshly cooked, never served. Ready for pickup."
            maxLength={1000}
          />
        </div>
        {error && (
          <p className="sb-field__error" role="alert">
            {error}
          </p>
        )}
        {justPublished && !error && (
          <p className="sb-muted" role="status">
            ✓ Published “{justPublished}” — see it under Active donations below.
          </p>
        )}
        <button type="button" className="sb-btn" onClick={submit}>
          Publish donation
        </button>
      </Card>

      <Card title="Active donations">
        {active.length === 0 ? (
          <p className="sb-muted">No active donations.</p>
        ) : (
          active.map((d) => (
            <DonationRow key={d.donationId} donation={d} actions={reservedNote(d)} />
          ))
        )}
      </Card>

      <Card title="Donation history">
        {history.length === 0 ? (
          <p className="sb-muted">No past donations yet.</p>
        ) : (
          history.map((d) => (
            <DonationRow key={d.donationId} donation={d} actions={reservedNote(d)} />
          ))
        )}
      </Card>

      <Card title="NGO directory">
        <p className="sb-section-intro">NGOs that can receive this restaurant's categories.</p>
        {DEMO_NGOS.filter((n) =>
          n.acceptedCategories.some((c) => restaurant.foodCategoriesServed.includes(c)),
        ).map((n) => (
          <div key={n.uid} className="sb-list-item">
            <div className="sb-row">
              <strong>{n.ngoName}</strong>
              <span className="sb-spacer" />
              <span className="sb-muted">{ngoRatingLabel(n.uid)}</span>
            </div>
            <div className="sb-row" style={{ marginTop: 6 }}>
              <span className="sb-muted">{n.representativeName}</span>
              <span className="sb-spacer" />
              <Phone number={n.phoneNumber} />
            </div>
          </div>
        ))}
      </Card>
    </>
  );
}

function NGODemo({
  uid,
  donations,
  onClaim,
  onCollect,
}: {
  uid: string;
  donations: Donation[];
  onClaim: (donationId: string, ngoUid: string) => void;
  onCollect: (donationId: string, ngoUid: string) => void;
}): JSX.Element {
  const ngo = ngoById(uid) as NGOProfile;

  // Donations available to this NGO are exactly those whose category is one of
  // the NGO's accepted categories (mirrors the real matching rule).
  const matched = donations.filter(
    (d) =>
      d.status === "Available" && eligibleNGOs([ngo], d.foodCategory).length > 0,
  );
  const myClaims = donations.filter(
    (d) => d.reservedBy === uid && (d.status === "Reserved" || d.status === "Collected"),
  );
  const collected = donations.filter(
    (d) => d.reservedBy === uid && d.status === "Completed",
  );
  const mealsCollected = totalImpactMeals(collected);

  return (
    <>
      <Card title={ngo.ngoName}>
        <p className="sb-muted" style={{ marginTop: 0 }}>
          Representative: {ngo.representativeName} · {ngo.address}
        </p>
        <div className="sb-row">
          {ngo.acceptedCategories.map((c) => (
            <CatPill key={c} value={c} />
          ))}
          <span className="sb-spacer" />
          <Phone number={ngo.phoneNumber} label="Contact" />
        </div>
        <p className="sb-muted" style={{ marginBottom: 0 }}>
          Average rating received: {ngoRatingLabel(uid)}
        </p>
      </Card>

      <Card title="Overview">
        <div className="sb-stats">
          <Stat label="Available to claim" value={matched.length} />
          <Stat label="My active claims" value={myClaims.length} />
          <Stat label="Completed pickups" value={collected.length} />
          <Stat label="Meals collected" value={mealsCollected} />
        </div>
      </Card>

      <Card title="Available donations (matched)">
        {matched.length === 0 ? (
          <p className="sb-muted">No matching donations available right now.</p>
        ) : (
          matched.map((d) => (
            <DonationRow
              key={d.donationId}
              donation={d}
              actions={
                <>
                  {fromRestaurant(d)}
                  <span className="sb-spacer" />
                  <button
                    type="button"
                    className="sb-btn"
                    onClick={() => onClaim(d.donationId, uid)}
                  >
                    Claim donation
                  </button>
                </>
              }
            />
          ))
        )}
      </Card>

      <Card title="My claims">
        {myClaims.length === 0 ? (
          <p className="sb-muted">You have no active claims.</p>
        ) : (
          myClaims.map((d) => (
            <DonationRow
              key={d.donationId}
              donation={d}
              actions={
                <>
                  {fromRestaurant(d)}
                  <span className="sb-spacer" />
                  <button
                    type="button"
                    className="sb-btn sb-btn--secondary"
                    onClick={() => onCollect(d.donationId, uid)}
                  >
                    Mark collected
                  </button>
                </>
              }
            />
          ))
        )}
      </Card>

      {collected.length > 0 && (
        <Card title="Completed pickups">
          {collected.map((d) => (
            <DonationRow key={d.donationId} donation={d} actions={fromRestaurant(d)} />
          ))}
        </Card>
      )}
    </>
  );
}

function reservedNote(d: Donation): JSX.Element | undefined {
  if (!d.reservedBy) return undefined;
  const n = ngoById(d.reservedBy);
  return (
    <span className="sb-muted">
      Reserved by {n ? n.ngoName : d.reservedBy}
      {n && (
        <>
          {" · "}
          <Phone number={n.phoneNumber} />
        </>
      )}
    </span>
  );
}

function fromRestaurant(d: Donation): JSX.Element {
  const r = restaurantById(d.restaurantUid);
  return (
    <span className="sb-muted">
      From {r ? r.restaurantName : d.restaurantUid}
      {r && (
        <>
          {" · "}
          <Phone number={r.phoneNumber} />
        </>
      )}
    </span>
  );
}

function ngoRatingLabel(uid: string): string {
  const result = computeRatingAverage(ratingsForNGO(uid).map((r) => r.stars));
  return result.hasRatings ? `${result.average.toFixed(1)} ★` : "Not yet rated";
}
